package br.com.vvrefeicoes.portal.feature.purchases

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelProductDetail
import br.com.vvrefeicoes.portal.domain.model.PurchasesPanelProductUnitPriceChange
import br.com.vvrefeicoes.portal.feature.operacional.ChartCard
import br.com.vvrefeicoes.portal.feature.operacional.HoverTooltip
import br.com.vvrefeicoes.portal.feature.operacional.ValueBar
import br.com.vvrefeicoes.portal.feature.operacional.compactMoney
import br.com.vvrefeicoes.portal.feature.operacional.rememberValueAxis
import io.github.koalaplot.core.bar.HorizontalBarPlot
import io.github.koalaplot.core.xygraph.CategoryAxisModel
import io.github.koalaplot.core.xygraph.XYGraph
import io.github.koalaplot.core.xygraph.rememberAxisContent
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.compras_col_product
import portalvv.shared.generated.resources.compras_panel_axis_variation_value
import portalvv.shared.generated.resources.compras_panel_col_average_price
import portalvv.shared.generated.resources.compras_panel_col_total_value
import portalvv.shared.generated.resources.compras_panel_col_variation_percent
import portalvv.shared.generated.resources.compras_panel_col_variation_value
import portalvv.shared.generated.resources.compras_panel_detailed_results
import portalvv.shared.generated.resources.compras_panel_no_data
import portalvv.shared.generated.resources.compras_panel_no_increase
import portalvv.shared.generated.resources.compras_panel_no_reduction
import portalvv.shared.generated.resources.compras_panel_top_increases_table
import portalvv.shared.generated.resources.compras_panel_top_reductions_table
import kotlin.math.abs
import kotlin.math.roundToLong

private const val PRODUCT_COLUMN_WEIGHT = 1.8f
private const val MONTH_COLUMN_WEIGHT = 3f
private const val MONTH_VALUE_WEIGHT = 1f
private const val AVERAGE_COLUMN_WEIGHT = 1.2f
private val CHART_SIDE_BY_SIDE_MIN_WIDTH = 900.dp

private data class ProductMonthDisplay(
    val totalCents: Long,
    val changeCents: Long?,
    val variationPercent: Double?,
)

@Composable
internal fun CategoryChangesSections(
    reductions: List<PurchasesPanelProductUnitPriceChange>,
    increases: List<PurchasesPanelProductUnitPriceChange>,
    months: List<MonthPeriod>,
) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
        CategoryChangeSection(
            changes = reductions,
            reduction = true,
            months = months,
        )
        CategoryChangeSection(
            changes = increases,
            reduction = false,
            months = months,
        )
    }
}

@Composable
private fun CategoryChangeSection(
    changes: List<PurchasesPanelProductUnitPriceChange>,
    reduction: Boolean,
    months: List<MonthPeriod>,
) {
    val sectionTitle =
        stringResource(
            if (reduction) {
                Res.string.compras_panel_top_reductions_table
            } else {
                Res.string.compras_panel_top_increases_table
            },
        )
    val emptyMessage =
        stringResource(
            if (reduction) {
                Res.string.compras_panel_no_reduction
            } else {
                Res.string.compras_panel_no_increase
            },
        )
    val orderedChanges =
        if (reduction) {
            changes.sortedBy { it.totalChangeCents }
        } else {
            changes.sortedByDescending { it.totalChangeCents }
        }
    val displaysByChange =
        orderedChanges.associateWith { change ->
            productMonthDisplays(change, months)
        }

    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
        Text(
            text = sectionTitle,
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
        )
        if (orderedChanges.isEmpty()) {
            Text(
                text = emptyMessage,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            return@Column
        }
        BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
            val chunkSize = monthsPerTable(maxWidth)
            val chunks = months.chunked(chunkSize)
            val chartBeside = chunkSize == 2 && maxWidth >= CHART_SIDE_BY_SIDE_MIN_WIDTH
            val tables = @Composable {
                Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                    chunks.forEachIndexed { index, chunk ->
                        CategoryChangeTableChunk(
                            changes = orderedChanges,
                            reduction = reduction,
                            months = chunk,
                            showAverage = index == chunks.lastIndex,
                            displaysByChange = displaysByChange,
                        )
                    }
                }
            }
            if (chartBeside) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
                    verticalAlignment = Alignment.Top,
                ) {
                    Box(modifier = Modifier.weight(1.35f)) {
                        tables()
                    }
                    CategoryChangeChart(
                        changes = orderedChanges,
                        reduction = reduction,
                        emptyMessage = emptyMessage,
                        modifier = Modifier.weight(1f),
                    )
                }
            } else {
                Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
                    tables()
                    CategoryChangeChart(
                        changes = orderedChanges,
                        reduction = reduction,
                        emptyMessage = emptyMessage,
                    )
                }
            }
        }
    }
}

@Composable
private fun CategoryChangeTableChunk(
    changes: List<PurchasesPanelProductUnitPriceChange>,
    reduction: Boolean,
    months: List<MonthPeriod>,
    showAverage: Boolean,
    displaysByChange: Map<PurchasesPanelProductUnitPriceChange, Map<MonthPeriod, ProductMonthDisplay>>,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            CategoryChangeHeader(months = months, showAverage = showAverage)
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            changes.forEachIndexed { index, change ->
                CategoryChangeRow(
                    change = change,
                    reduction = reduction,
                    months = months,
                    showAverage = showAverage,
                    displays = displaysByChange[change].orEmpty(),
                )
                if (index < changes.lastIndex) {
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                }
            }
        }
    }
}

@Composable
private fun CategoryChangeHeader(
    months: List<MonthPeriod>,
    showAverage: Boolean,
) {
    Column(modifier = Modifier.fillMaxWidth().background(AppPalette.Neutral50)) {
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(horizontal = AppSpacing.space8, vertical = AppSpacing.space8),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = stringResource(Res.string.compras_col_product),
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.weight(PRODUCT_COLUMN_WEIGHT),
            )
            months.forEach { month ->
                Text(
                    text = MonthCalendar.shortLabel(month),
                    style = MaterialTheme.typography.labelSmall,
                    fontWeight = FontWeight.SemiBold,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.weight(MONTH_COLUMN_WEIGHT),
                )
            }
            if (showAverage) {
                Text(
                    text = stringResource(Res.string.compras_panel_col_average_price),
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    textAlign = TextAlign.End,
                    modifier = Modifier.weight(AVERAGE_COLUMN_WEIGHT).padding(horizontal = AppSpacing.space4),
                )
            }
        }
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(horizontal = AppSpacing.space8, vertical = AppSpacing.space4),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Spacer(modifier = Modifier.weight(PRODUCT_COLUMN_WEIGHT))
            months.forEach {
                Row(modifier = Modifier.weight(MONTH_COLUMN_WEIGHT)) {
                    CategoryHeaderCell(stringResource(Res.string.compras_panel_col_total_value))
                    CategoryHeaderCell(stringResource(Res.string.compras_panel_col_variation_percent))
                    CategoryHeaderCell(stringResource(Res.string.compras_panel_col_variation_value))
                }
            }
            if (showAverage) {
                Spacer(modifier = Modifier.weight(AVERAGE_COLUMN_WEIGHT))
            }
        }
    }
}

@Composable
private fun RowScope.CategoryHeaderCell(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelSmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        textAlign = TextAlign.End,
        modifier = Modifier.weight(MONTH_VALUE_WEIGHT).padding(horizontal = 2.dp),
    )
}

@Composable
private fun CategoryChangeRow(
    change: PurchasesPanelProductUnitPriceChange,
    reduction: Boolean,
    months: List<MonthPeriod>,
    showAverage: Boolean,
    displays: Map<MonthPeriod, ProductMonthDisplay>,
) {
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(horizontal = AppSpacing.space8, vertical = AppSpacing.space8),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = change.productName,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurface,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(PRODUCT_COLUMN_WEIGHT),
        )
        months.forEach { month ->
            val value = displays[month]
            Row(modifier = Modifier.weight(MONTH_COLUMN_WEIGHT)) {
                CategoryMonthValues(value = value, reduction = reduction)
            }
        }
        if (showAverage) {
            Text(
                text =
                    if (change.averageUnitPriceCents > 0L) {
                        money(change.averageUnitPriceCents)
                    } else {
                        NO_TABLE_VALUE
                    },
                style = MaterialTheme.typography.bodySmall,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.onSurface,
                textAlign = TextAlign.End,
                modifier = Modifier.weight(AVERAGE_COLUMN_WEIGHT).padding(horizontal = AppSpacing.space4),
            )
        }
    }
}

@Composable
private fun RowScope.CategoryMonthValues(
    value: ProductMonthDisplay?,
    reduction: Boolean,
) {
    val changeColor =
        when {
            value?.changeCents == null -> MaterialTheme.colorScheme.onSurfaceVariant
            value.changeCents < 0L -> AppPalette.Success900
            value.changeCents > 0L -> AppPalette.Error900
            else -> MaterialTheme.colorScheme.onSurface
        }
    CategoryValueCell(
        text = value?.takeIf { it.totalCents > 0L }?.let { money(it.totalCents) } ?: NO_TABLE_VALUE,
        color = MaterialTheme.colorScheme.onSurface,
    )
    CategoryValueCell(
        text = value?.variationPercent?.let { variationText(it) } ?: NO_TABLE_VALUE,
        color =
            when {
                value?.variationPercent == null -> MaterialTheme.colorScheme.onSurfaceVariant
                reduction -> AppPalette.Success900
                else -> AppPalette.Error900
            },
    )
    CategoryValueCell(
        text = value?.changeCents?.let { signedMoney(it) } ?: NO_TABLE_VALUE,
        color = changeColor,
    )
}

@Composable
private fun RowScope.CategoryValueCell(
    text: String,
    color: Color,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodySmall,
        color = color,
        textAlign = TextAlign.End,
        modifier = Modifier.weight(MONTH_VALUE_WEIGHT).padding(horizontal = 2.dp),
    )
}

private fun productMonthDisplays(
    change: PurchasesPanelProductUnitPriceChange,
    months: List<MonthPeriod>,
): Map<MonthPeriod, ProductMonthDisplay> {
    var previousTotalCents: Long? = null
    return months.associateWith { period ->
        val totalCents =
            change.monthly
                .firstOrNull { it.year == period.year && it.month == period.month }
                ?.totalCents ?: 0L
        val previous = previousTotalCents
        val changeCents =
            if (totalCents > 0L && previous != null) {
                totalCents - previous
            } else {
                null
            }
        val variationPercent =
            if (changeCents != null && previous != null && previous > 0L) {
                changeCents * 100.0 / previous
            } else {
                null
            }
        if (totalCents > 0L) {
            previousTotalCents = totalCents
        }
        ProductMonthDisplay(
            totalCents = totalCents,
            changeCents = changeCents,
            variationPercent = variationPercent,
        )
    }
}

@Composable
private fun CategoryChangeChart(
    changes: List<PurchasesPanelProductUnitPriceChange>,
    reduction: Boolean,
    emptyMessage: String,
    modifier: Modifier = Modifier,
) {
    ChartCard(modifier = modifier) {
        if (changes.isEmpty()) {
            Text(
                text = emptyMessage,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        } else {
            val chartChanges = changes.sortedByDescending { abs(it.totalChangeCents) }
            val ordered = chartChanges.reversed()
            val names = ordered.map { it.productName }
            val values = ordered.map { abs(it.totalChangeCents) / 100f }
            val barColor = if (reduction) AppPalette.Success500 else AppPalette.Error500
            XYGraph(
                gridStyle = NoGridStyle,
                xAxisModel = rememberValueAxis(values.maxOrNull() ?: 0f, headroom = 1.2f),
                yAxisModel = CategoryAxisModel(names),
                xAxisContent =
                    rememberAxisContent(
                        title = {
                            Text(
                                stringResource(Res.string.compras_panel_axis_variation_value),
                                style = MaterialTheme.typography.labelSmall,
                            )
                        },
                        labels = { value ->
                            Text(
                                compactMoney((value * 100).roundToLong()),
                                style = MaterialTheme.typography.labelSmall,
                            )
                        },
                    ),
                yAxisContent = rememberAxisContent(labels = { name -> Text(name, style = MaterialTheme.typography.labelSmall) }),
                modifier = Modifier.fillMaxWidth().height((changes.size * 44 + 48).dp),
            ) {
                HorizontalBarPlot(
                    xData = values,
                    yData = names,
                    barWidth = 0.6f,
                    bar = { index, _, _ ->
                        val change = ordered.getOrNull(index)
                        val label =
                            if (change == null) {
                                ""
                            } else {
                                money(abs(change.totalChangeCents))
                            }
                        val tooltip =
                            if (change == null) {
                                ""
                            } else {
                                "${change.productName}\n" +
                                    "${money(change.previousTotalCents)} -> ${money(change.currentTotalCents)}\n" +
                                    label
                            }
                        HoverTooltip(text = tooltip) {
                            ValueBar(
                                color = barColor,
                                label = label,
                                horizontal = true,
                            )
                        }
                    },
                )
            }
        }
    }
}

internal fun signedMoney(cents: Long): String {
    if (cents == 0L) {
        return money(0L)
    }
    val sign = if (cents > 0L) "+" else "-"
    return "$sign${money(abs(cents))}"
}

@Composable
internal fun MultimonthProductDetailsSection(
    details: List<PurchasesPanelProductDetail>,
    months: List<MonthPeriod>,
) {
    var query by remember(details) { mutableStateOf("") }
    val filtered = remember(details, query) { filterProductDetails(details, query) }
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
        Text(
            text = stringResource(Res.string.compras_panel_detailed_results),
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface,
        )
        ProductDetailsSearch(query = query, onQueryChange = { query = it })
        if (filtered.isEmpty()) {
            Text(
                text = stringResource(Res.string.compras_panel_no_data),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        } else {
            BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
                val chunkSize = monthsPerTable(maxWidth)
                Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                    months.chunked(chunkSize).forEachIndexed { index, chunk ->
                        ProductDetailsTableChunk(
                            details = filtered,
                            months = chunk,
                            showAverage = index == months.chunked(chunkSize).lastIndex,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ProductDetailsTableChunk(
    details: List<PurchasesPanelProductDetail>,
    months: List<MonthPeriod>,
    showAverage: Boolean,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            CategoryChangeHeader(months = months, showAverage = showAverage)
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            details.forEachIndexed { index, detail ->
                ProductDetailRow(
                    detail = detail,
                    months = months,
                    showAverage = showAverage,
                )
                if (index < details.lastIndex) {
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                }
            }
        }
    }
}

@Composable
private fun ProductDetailRow(
    detail: PurchasesPanelProductDetail,
    months: List<MonthPeriod>,
    showAverage: Boolean,
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space8, vertical = AppSpacing.space8),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = detail.productName,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurface,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(PRODUCT_COLUMN_WEIGHT),
        )
        months.forEach { month ->
            val value = detail.monthly.firstOrNull { it.year == month.year && it.month == month.month }
            Row(modifier = Modifier.weight(MONTH_COLUMN_WEIGHT)) {
                val changeColor =
                    when {
                        value?.changeCents == null -> MaterialTheme.colorScheme.onSurfaceVariant
                        value.changeCents < 0L -> AppPalette.Success900
                        value.changeCents > 0L -> AppPalette.Error900
                        else -> MaterialTheme.colorScheme.onSurface
                    }
                CategoryValueCell(
                    text =
                        value?.takeIf { it.unitPriceCents > 0L }?.let { money(it.unitPriceCents) }
                            ?: NO_TABLE_VALUE,
                    color = MaterialTheme.colorScheme.onSurface,
                )
                CategoryValueCell(
                    text = value?.variationPercent?.let { variationText(it) } ?: NO_TABLE_VALUE,
                    color =
                        when {
                            value?.variationPercent == null -> MaterialTheme.colorScheme.onSurfaceVariant
                            value.variationPercent < 0.0 -> AppPalette.Success900
                            value.variationPercent > 0.0 -> AppPalette.Error900
                            else -> MaterialTheme.colorScheme.onSurface
                        },
                )
                CategoryValueCell(
                    text = value?.changeCents?.let { signedMoney(it) } ?: NO_TABLE_VALUE,
                    color = changeColor,
                )
            }
        }
        if (showAverage) {
            Text(
                text =
                    if (detail.averageUnitPriceCents > 0L) {
                        money(detail.averageUnitPriceCents)
                    } else {
                        NO_TABLE_VALUE
                    },
                style = MaterialTheme.typography.bodySmall,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.onSurface,
                textAlign = TextAlign.End,
                modifier = Modifier.weight(AVERAGE_COLUMN_WEIGHT).padding(horizontal = AppSpacing.space4),
            )
        }
    }
}
